/**
 * Classifies who answered an outbound call from loudness frames, the same way
 * Twilio's answering machine detection does: a short first utterance followed
 * by silence is a person ("Hello?"); long continuous speech is a machine
 * (voicemail greeting, phone menu, Apple/Google call screening).
 *
 * Tuned toward caution: anything unclear is "unknown" and must not auto-play.
 */

export const ANSWER_DETECTION_DEFAULTS = {
  // A first utterance longer than this is treated as a machine.
  maxHumanUtteranceMs: 2400,
  // Silence after speech that ends the first utterance.
  speechEndSilenceMs: 1200,
  // Speech must persist this long to count as an utterance (ignores clicks).
  minUtteranceMs: 120,
  // Brief dips shorter than this don't end the onset window.
  onsetGapToleranceMs: 80,
  // No speech at all within this window gives "unknown".
  initialSilenceTimeoutMs: 5000,
  // Hard cap on how long detection runs.
  overallTimeoutMs: 8000,
  // Rep speech for this long before a verdict cancels auto-play.
  repSpeechCancelMs: 250,
  // RMS (0..1) floors for speech; the adaptive noise floor can raise them.
  remoteSpeechRmsMin: 0.015,
  micSpeechRmsMin: 0.02,
  noiseFloorMultiplier: 4,
  initialNoiseFloor: 0.003,
};

export const VERDICT = {
  HUMAN: "human",
  MACHINE: "machine",
  UNKNOWN: "unknown",
  REP_SPOKE: "rep_spoke",
};

function createLevelTracker(minRms, config) {
  let noiseFloor = config.initialNoiseFloor;
  return (rms) => {
    const threshold = Math.max(minRms, noiseFloor * config.noiseFloorMultiplier);
    const isSpeech = rms >= threshold;
    if (!isSpeech) {
      noiseFloor = rms < noiseFloor ? noiseFloor * 0.8 + rms * 0.2 : noiseFloor * 0.98 + rms * 0.02;
    }
    return isSpeech;
  };
}

/**
 * @param {Partial<typeof ANSWER_DETECTION_DEFAULTS>} overrides
 * @returns {{ push: (frame: { remote: number, mic: number, ms: number }) => object | null, stats: () => object }}
 */
export function createAnswerClassifier(overrides = {}) {
  const config = { ...ANSWER_DETECTION_DEFAULTS, ...overrides };
  const isRemoteSpeech = createLevelTracker(config.remoteSpeechRmsMin, config);
  const isMicSpeech = createLevelTracker(config.micSpeechRmsMin, config);

  let elapsed = 0;
  let onsetRun = 0;
  let onsetGap = 0;
  let utteranceStart = null;
  let lastSpeechEnd = null;
  let micRun = 0;
  let verdict = null;

  const stats = () => ({
    elapsed_ms: Math.round(elapsed),
    first_utterance_ms:
      utteranceStart == null ? null : Math.round((lastSpeechEnd ?? elapsed) - utteranceStart),
    silence_after_ms:
      lastSpeechEnd == null || utteranceStart == null ? null : Math.round(elapsed - lastSpeechEnd),
  });

  const decide = (v, reason) => {
    verdict = { verdict: v, reason, ...stats() };
    return verdict;
  };

  const push = ({ remote = 0, mic = 0, ms }) => {
    if (verdict) return verdict;
    elapsed += ms;

    if (isMicSpeech(mic)) {
      micRun += ms;
      if (micRun >= config.repSpeechCancelMs) return decide(VERDICT.REP_SPOKE, "rep_spoke_first");
    } else {
      micRun = 0;
    }

    const speaking = isRemoteSpeech(remote);

    if (utteranceStart == null) {
      if (speaking) {
        onsetRun += ms;
        onsetGap = 0;
        if (onsetRun >= config.minUtteranceMs) {
          utteranceStart = elapsed - onsetRun;
          lastSpeechEnd = elapsed;
        }
      } else if (onsetRun > 0) {
        onsetGap += ms;
        if (onsetGap > config.onsetGapToleranceMs) {
          onsetRun = 0;
          onsetGap = 0;
        }
      }
      if (utteranceStart == null && elapsed >= config.initialSilenceTimeoutMs) {
        return decide(VERDICT.UNKNOWN, "no_speech");
      }
    } else {
      if (speaking) {
        lastSpeechEnd = elapsed;
      }
      if (lastSpeechEnd - utteranceStart > config.maxHumanUtteranceMs) {
        return decide(VERDICT.MACHINE, "long_utterance");
      }
      if (!speaking && elapsed - lastSpeechEnd >= config.speechEndSilenceMs) {
        return decide(VERDICT.HUMAN, "short_utterance_then_silence");
      }
    }

    if (elapsed >= config.overallTimeoutMs) {
      return decide(VERDICT.UNKNOWN, "timeout");
    }
    return null;
  };

  return { push, stats };
}
